import { flow, Instance, getEnv, types } from "mobx-state-tree";

import { DeletedDataResponse } from "../../api-models/response/organization/DeletedDataResponse";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { Column, ColumnType } from "../../components/datatable/interfaces";
import { endpoints } from "../api/ApiEndpoints";
import { DeletedDataItem } from "./DeletedDataItem";
import { RestoreResponse } from "../../api-models/response/organization/RestoreResponse";

export const DeletedDataStorageModel = types
  .compose(
    DataGridStorageModel,
    types.model({
      isRestoreInProgress: types.optional(types.boolean, false),
      isDeleteInProgress: types.optional(types.boolean, false),
      restoreMessages: types.array(types.frozen<RestoreResponse>()),
    })
  )
  .named("DeletedDataStorage")
  .views(() => ({
    get tableColumns(): Column[] {
      return [
        {
          key: "dateDeleted",
          name: "general.deleted-data.list-header.data-deleted",
          type: ColumnType.DateTime,
          sortable: true,
          filterable: true,
        },
        {
          key: "deletedBy",
          name: "general.deleted-data.list-header.deleted-by",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "dataType",
          name: "general.deleted-data.list-header.data-type",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "identifier",
          name: "general.deleted-data.list-header.identifier",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "details",
          name: "general.deleted-data.list-header.details",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
          containsLink: true,
        },
      ];
    },
  }))
  .actions((self) => {
    return {
      mapItem(item: DeletedDataResponse): DeletedDataItem {
        return new DeletedDataItem(item);
      },
      afterCreate() {
        self.gridColumns = self.tableColumns;
        self.defaultSort = { field: "dateDeleted", sort: "desc" };
      },
      restoreDeletedData: flow(function* (deletedData: any[]) {
        try {
          self.isRestoreInProgress = true;
          const endpoint = endpoints.get("restore_deleted_data");
          const response = yield getEnv(self).api.put(endpoint, deletedData);
          self.deselectAll();
          yield self.load();
          self.restoreMessages.replace(response.data as RestoreResponse[]);
        } catch (error) {
          throw error;
        } finally {
          self.isRestoreInProgress = false;
        }
      }),
      hardDeleteData: flow(function* (deletedData: any[]) {
        try {
          self.isDeleteInProgress = true;
          const endpoint = endpoints.get("hard_delete_data");
          yield getEnv(self).api.put(endpoint, deletedData);
          self.deselectAll();
          yield self.load();
        } catch (error) {
          throw error;
        } finally {
          self.isDeleteInProgress = false;
        }
      }),
    };
  })
  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("deleted_data");
      return endpoint;
    },
    get errorRestoreMessages() {
      return self.restoreMessages.filter((m) => m.isError);
    },
    get warningRestoreMessages() {
      const warnings = self.restoreMessages.filter((m) => !m.isError);
      return Array.from(new Set(warnings.map((x) => x.messageKey)));
    },
  }));

export type DeletedDataStorage = Instance<typeof DeletedDataStorageModel>;
